/**
 * frontend/src/screens/PSPCompaniesTab.js  (Phase 12 → 12g)
 *
 * Admin → PSP Companies (business). One card per PSP company:
 *   - name, active, contact, address, notes
 *   - users attached (attach/detach on the PSP Users tab), open item count
 *   - working agreements: RAW model review rules, Meshy sharing
 * Technical integration (API access, keys, webhooks, API log) lives on
 * Admin → API Integrations.
 */
import React, { useEffect, useState } from 'react';
import { getPSPCompanies, createPSPCompany, updatePSPCompany } from '../api';
import { Spinner } from '../components/UI';

const card  = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, marginBottom: 16 };
const small = { fontSize: 12, color: 'var(--text2)' };
const errMsg = (e, f) => e?.response?.data?.error || f;

const CONTACT_FIELDS = ['name', 'contactName', 'contactTitle', 'contactPhone', 'contactEmail',
  'addressCompanyName', 'address1', 'address2', 'city', 'state', 'zip', 'country', 'notes'];

// Phase 12b — RAW model review settings (per PSP company)
function ReviewSettings({ c, toast, onChanged }) {
  const init = () => ({
    rawReviewEnforced: !!c.rawReviewEnforced,
    reviewWindowHours: c.reviewWindowHours ?? 24,
    reviewExcludeWeekends: c.reviewExcludeWeekends !== false,
    reviewAlertEmails: c.reviewAlertEmails || '',
  });
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(init()); }, [c.rawReviewEnforced, c.reviewWindowHours, c.reviewExcludeWeekends, c.reviewAlertEmails]);
  const orig = init();
  const dirty = JSON.stringify(f) !== JSON.stringify(orig);
  const save = async () => {
    setSaving(true);
    try { await updatePSPCompany(c.id, { ...f, reviewWindowHours: Number(f.reviewWindowHours) }); toast('Review settings saved', 'success'); onChanged(); }
    catch (e) { toast(errMsg(e, 'Save failed'), 'error'); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '0.5px solid var(--border)' }}>
      <div className="label" style={{ marginBottom: 6 }}>RAW model review</div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13 }}>
        <input type="checkbox" checked={f.rawReviewEnforced} onChange={e => setF(p => ({ ...p, rawReviewEnforced: e.target.checked }))} style={{ marginTop: 3 }} />
        <span>Enforce review deadline — RAW models not decided in time are <strong>accepted automatically</strong>, and the PSP can't move past RAW until it's accepted</span>
      </label>
      {f.rawReviewEnforced && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, marginLeft: 22 }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            Window
            <input className="input" type="number" min={1} max={720} value={f.reviewWindowHours}
              onChange={e => setF(p => ({ ...p, reviewWindowHours: e.target.value }))} style={{ width: 70 }} />
            hours
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={f.reviewExcludeWeekends} onChange={e => setF(p => ({ ...p, reviewExcludeWeekends: e.target.checked }))} />
            Weekend hours don't count (US Eastern)
          </label>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
          Alert emails for new RAW models (comma-separated; empty = no emails; at most one email per 15 minutes)
        </div>
        <input className="input" value={f.reviewAlertEmails} onChange={e => setF(p => ({ ...p, reviewAlertEmails: e.target.value }))}
          placeholder="ops@solidiphy.com, sharon@solidiphy.com" />
      </div>
      {dirty && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save review settings'}</button>
          <button className="btn btn-sm" onClick={() => setF(init())}>Cancel</button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, wide, type = 'text' }) {
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined }}>
      <div className="label">{label}</div>
      <input className="input" type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function CompanyCard({ c, toast, onChanged }) {
  const init = () => Object.fromEntries(CONTACT_FIELDS.map(k => [k, c[k] || '']));
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { setF(init()); }, [c.updatedAt]);
  const dirty = CONTACT_FIELDS.some(k => (f[k] || '') !== (c[k] || ''));
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));

  const run = async (fn, okMsg) => {
    try { const r = await fn(); if (okMsg) toast(okMsg, 'success'); onChanged(); return r; }
    catch (e) { toast(errMsg(e, 'Failed'), 'error'); return null; }
  };
  const save = async () => {
    if (!f.name.trim()) return toast('Name is required', 'error');
    setSaving(true);
    await run(() => updatePSPCompany(c.id, f), 'Saved');
    setSaving(false);
  };
  const contactLine = [c.contactName, c.contactPhone, c.contactEmail].filter(Boolean).join(' · ');

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
          <input type="checkbox" checked={c.isActive} onChange={e => run(() => updatePSPCompany(c.id, { isActive: e.target.checked }), e.target.checked ? 'Activated' : 'Deactivated')} />
          Active
        </label>
        <span style={{ ...small, color: c.apiEnabled ? '#065F46' : 'var(--text3)' }}>{c.apiEnabled ? 'Connected via API' : 'No API connection'}</span>
        <span style={{ ...small, marginLeft: 'auto' }}>{c.openItemCount} open item{c.openItemCount !== 1 ? 's' : ''} assigned</span>
        <button className="btn btn-sm" onClick={() => setOpen(o => !o)}>{open ? 'Close details' : 'Edit details'}</button>
      </div>
      {!open && <div style={{ ...small, marginTop: 6 }}>{contactLine || <em>No contact details yet</em>}</div>}

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Company name" value={f.name} onChange={set('name')} />
            <div />
            <Field label="Contact name"  value={f.contactName}  onChange={set('contactName')} />
            <Field label="Contact title" value={f.contactTitle} onChange={set('contactTitle')} />
            <Field label="Phone" value={f.contactPhone} onChange={set('contactPhone')} />
            <Field label="Email" value={f.contactEmail} onChange={set('contactEmail')} type="email" />
            <Field label="Company name on address" value={f.addressCompanyName} onChange={set('addressCompanyName')} wide />
            <Field label="Address line 1" value={f.address1} onChange={set('address1')} wide />
            <Field label="Address line 2" value={f.address2} onChange={set('address2')} wide />
            <Field label="City" value={f.city} onChange={set('city')} />
            <Field label="State / province" value={f.state} onChange={set('state')} />
            <Field label="ZIP / postal code" value={f.zip} onChange={set('zip')} />
            <Field label="Country" value={f.country} onChange={set('country')} placeholder="US" />
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="label">Notes</div>
              <textarea className="input" rows={3} value={f.notes} onChange={e => set('notes')(e.target.value)}
                placeholder="Agreement details, account numbers, anything the team should know" />
            </div>
          </div>
          {dirty && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save details'}</button>
              <button className="btn btn-sm" onClick={() => setF(init())}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {/* Working agreements (business rules for API partners) */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '0.5px solid var(--border)' }}>
        <div className="label" style={{ marginBottom: 6 }}>Files shared with this PSP</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Customer photos are always shared. The PSP portal always shows Meshy files; this setting is for API partners.</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={!!c.shareMeshyViaApi} style={{ marginTop: 3 }}
            onChange={e => run(() => updatePSPCompany(c.id, { shareMeshyViaApi: e.target.checked }), e.target.checked ? 'Meshy models shared for all items' : 'Meshy models no longer shared by default')} />
          <span>Share Meshy models for <strong>all</strong> assigned items (otherwise share per item from Operations)</span>
        </label>
      </div>
      <ReviewSettings c={c} toast={toast} onChanged={onChanged} />

      <div style={{ marginTop: 12, ...small }}>
        Users: {c.users?.length ? c.users.map(u => `${u.name}${u.isActive ? '' : ' (inactive)'}`).join(', ') : <em>none — attach users on the PSP Users tab</em>}
      </div>
    </div>
  );
}

export default function PSPCompaniesTab({ toast }) {
  const [companies, setCompanies] = useState(null);
  const [newName, setNewName]     = useState('');

  const load = () => getPSPCompanies().then(r => setCompanies(r.data)).catch(() => { setCompanies([]); toast('Failed to load PSP companies', 'error'); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    try { await createPSPCompany({ name: newName.trim() }); setNewName(''); toast('PSP company created', 'success'); load(); }
    catch (e) { toast(errMsg(e, 'Create failed'), 'error'); }
  };

  if (!companies) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>PSP Companies</div>
        <input className="input" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()}
          placeholder="New PSP company name" style={{ width: 240, marginLeft: 'auto' }} />
        <button className="btn btn-primary btn-sm" onClick={create} disabled={!newName.trim()}>+ Add</button>
      </div>
      {companies.length === 0 && <div style={{ ...card, textAlign: 'center', color: 'var(--text3)' }}>No PSP companies yet</div>}
      {companies.map(c => <CompanyCard key={c.id} c={c} toast={toast} onChanged={load} />)}
    </div>
  );
}
